import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import AddInputs from "../Add/AddInputs"
import Button from "../buttons/Button"
import ButtonWithImg from "../buttons/ButtonWithImg"
import SecondaryButton from "../buttons/SecondaryButton"
import MainModel from "../Models/MainModel"
import { setCoordinates, setLocalErrors } from "../../store/slices/geolocationSlice"
import { RootState } from "../../store"

interface CurrentLocationProps {
  handleClose: () => void
  getCurrentLocation: () => void
  handleGetAltitude: () => void
  inputCordinates: { latitude: number | null; longitude: number | null }
  setInputCordinates: React.Dispatch<React.SetStateAction<{ latitude: number | null; longitude: number | null }>>
}

export default function CurrentLocation({ 
  handleClose, 
  getCurrentLocation, 
  handleGetAltitude, 
  inputCordinates, 
  setInputCordinates 
}: CurrentLocationProps) {
  const dispatch = useDispatch()
  const { coordinates, error, localErrors } = useSelector(
    (state: RootState) => state.geolocation,
  )
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleClose])

  const inputs = {
    "": {
      Latitude: {
        required: true,
        disabled: false,
        id: "latitude",
        type: "input",
        tabIndex: 0,
      },
      Longitude: {
        required: true,
        disabled: false,
        id: "longitude",
        type: "input",
        tabIndex: 1,
      },
    },
  }

  const onInputChange = (id: string, value: string) => {
    // Accept digits, an optional negative sign at start, and up to one decimal point
    const numericRegex = /^-?\d*\.?\d*$|^-$/
    
    if (value === "") {
      // Handle empty input case
      setInputCordinates({ ...inputCordinates, [id]: null })
      dispatch(setLocalErrors({ ...localErrors, [id]: "" }))
      return
    }
    
    // Check if the input matches our regex (valid number format)
    if (numericRegex.test(value)) {
      // For decimal inputs, check decimal places
      if (value.includes('.')) {
        const [whole, decimal] = value.split('.')
        if (decimal && decimal.length > 6) {
          // dispatch(setLocalErrors({ ...localErrors, [id]: "Maximum 6 decimal places allowed." }))
          return
        }
        // Allow typing decimal point
        if (value.endsWith('.')) {
          setInputCordinates({ ...inputCordinates, [id]: value === '.' ? 0 : value })
        } else {
          const numericValue = parseFloat(value)
          if ((id === 'latitude' && (numericValue < -90 || numericValue > 90)) ||
              (id === 'longitude' && (numericValue < -180 || numericValue > 180))) {
            dispatch(setLocalErrors({ ...localErrors, [id]: `${id === 'latitude' ? 'Latitude must be between -90 and 90' : 'Longitude must be between -180 and 180'}`}))
            return
          }
          setInputCordinates({ ...inputCordinates, [id]: numericValue })
        }
      } else if (value === '-') {
        setInputCordinates({ ...inputCordinates, [id]: value })
      } else {
        // No decimal point, safe to convert to number
        const numericValue = parseFloat(value)
        if ((id === 'latitude' && (numericValue < -90 || numericValue > 90)) ||
            (id === 'longitude' && (numericValue < -180 || numericValue > 180))) {
          dispatch(setLocalErrors({ ...localErrors, [id]: `${id === 'latitude' ? 'Latitude must be between -90 and 90' : 'Longitude must be between -180 and 180'}`}))
          return
        }
        setInputCordinates({ ...inputCordinates, [id]: numericValue })
      }
      dispatch(setLocalErrors({ ...localErrors, [id]: "" }))
    } else {
      // Invalid format - don't update coordinates but set error
      dispatch(setLocalErrors({ ...localErrors, [id]: "Please enter a valid number." }))
    }
  }    
  const validateCoordinates = () => {
    const errors: Record<string, string> = {}
    
    // Validate latitude
    if (inputCordinates.latitude === null) {
      errors.latitude = "Latitude is required"
    } else if (isNaN(inputCordinates.latitude)) {
      errors.latitude = "Please enter a valid number"
    } else if (inputCordinates.latitude < -90 || inputCordinates.latitude > 90) {
      errors.latitude = "Latitude must be between -90 and 90 degrees"
    }

    // Validate longitude
    if (inputCordinates.longitude === null) {
      errors.longitude = "Longitude is required"
    } else if (isNaN(inputCordinates.longitude)) {
      errors.longitude = "Please enter a valid number"
    } else if (inputCordinates.longitude < -180 || inputCordinates.longitude > 180) {
      errors.longitude = "Longitude must be between -180 and 180 degrees"
    }

    dispatch(setLocalErrors(errors))
    return Object.keys(errors).length === 0
  }

  const handleGetAltitudeClick = () => {
    if (validateCoordinates()) {
      // Save coordinates to store before getting altitude
      if (inputCordinates.latitude !== null && inputCordinates.longitude !== null) {
        dispatch(setCoordinates({
          latitude: inputCordinates.latitude,
          longitude: inputCordinates.longitude
        }))
      }
      handleGetAltitude()
      handleClose()
    }
  }

  useEffect(() => {
    if (coordinates.latitude !== null && coordinates.longitude !== null) {
      setInputCordinates({ 
        latitude: coordinates.latitude, 
        longitude: coordinates.longitude 
      })
    } else {
      setInputCordinates({ latitude: null, longitude: null })
    }
    // Clear any existing errors when component mounts or coordinates change
    dispatch(setLocalErrors({ latitude: '', longitude: '' }))
  }, [coordinates, dispatch, setInputCordinates])

  return (
    <MainModel modelWidth={"w-[744px]"}>
      <div ref={modalRef}>
        <div className="flex items-center justify-between pb-3.5 border-b mb-2">
          <h1 className="text-heading5">Current Location</h1>
          <ButtonWithImg
            testId="use_my_current_location"
            ImgSrc="map-pin"
            buttonName="Use my current location"
            className="px-4"
            buttonOnclick={getCurrentLocation}
          />
        </div>
        <AddInputs
          inputs={inputs}
          totalInputCount={2}
          inputValues={{
            latitude: inputCordinates?.latitude?.toString() || "",
            longitude: inputCordinates?.longitude?.toString() || "",
          }}
          errors={localErrors}
          onInputChange={onInputChange}
        />
        {error && <p className="text-red-500 mt-2">{error}</p>}
        <div className="flex mt-8 gap-6">
          <SecondaryButton
            buttonName={"Cancel"}
            className="px-4 py-2 h-fit"
            handleClick={handleClose}
            testId="cancel-btn"
            buttonDisabled={false}
          />
          <Button
            buttonName={"Get Altitude"}
            className="py-2.5 h-fit px-4 rounded-[6px]"
            textSize={"text-large"}
            buttonOnclick={handleGetAltitudeClick}
            testId={`get-altitude`}
            loading={false}
          />
        </div>
      </div>
    </MainModel>
  )
}